import { api, formatTenne, openInvoicePdf } from "@awtoban/shared";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "@awtoban/ui";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Printer } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface RecentRow {
  id: string;
  invoice_number: string;
  created_at: string;
  vehicle_plate: string;
  total_tenne: number;
  status: string;
}

interface EOSSummary {
  operator: string;
  station: string;
  shift_start: string;
  now: string;
  tx_count: number;
  total_revenue_tenne: number;
  by_status: { issued: number; paid: number; void: number };
}

/** Reprint dropdown — pulls the last 10 station transactions; clicking opens
 * the invoice PDF in a new tab and audit-logs the reprint. */
export function ReprintMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const recent = useQuery({
    queryKey: ["operator-recent"],
    enabled: open,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/operator/recent", {});
      return (data as RecentRow[] | undefined) ?? [];
    },
  });

  const reprint = async (tx: RecentRow) => {
    await api.POST("/api/v1/operator/transactions/{tx_uuid}/reprint", {
      params: { path: { tx_uuid: tx.id } },
    } as never);
    await openInvoicePdf(tx.id);
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Printer className="h-4 w-4" />
        {t("menu.reprint")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("menu.reprint_title")}</DialogTitle>
          </DialogHeader>
          {recent.isLoading ? (
            <p className="text-ink-muted text-sm">{t("menu.loading")}</p>
          ) : recent.data && recent.data.length > 0 ? (
            <ul className="divide-y divide-line">
              {recent.data.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2">
                  <div>
                    <div className="tabular font-medium">{r.invoice_number}</div>
                    <div className="text-[12px] text-ink-muted">
                      {new Date(r.created_at).toLocaleString()} · {r.vehicle_plate}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tabular">{formatTenne(r.total_tenne)} TMT</span>
                    <Button size="sm" variant="secondary" onClick={() => reprint(r)}>
                      {t("menu.reprint")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-muted text-sm">{t("menu.no_recent")}</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** End-of-shift report — totals for the current operator's calendar day. */
export function EndOfShiftMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const summary = useQuery({
    queryKey: ["operator-eos"],
    enabled: open,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/operator/end-of-shift", {});
      return data as unknown as EOSSummary;
    },
  });

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <ClipboardList className="h-4 w-4" />
        {t("menu.end_of_shift")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("menu.eos_title")}</DialogTitle>
          </DialogHeader>
          {summary.isLoading || !summary.data ? (
            <p className="text-ink-muted text-sm">{t("menu.loading")}</p>
          ) : (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-y-1 text-sm">
                <dt className="text-ink-muted">{t("menu.eos_operator")}</dt>
                <dd className="font-medium">{summary.data.operator}</dd>
                <dt className="text-ink-muted">{t("menu.eos_station")}</dt>
                <dd className="font-medium">{summary.data.station}</dd>
                <dt className="text-ink-muted">{t("menu.eos_shift_start")}</dt>
                <dd className="tabular">{new Date(summary.data.shift_start).toLocaleString()}</dd>
                <dt className="text-ink-muted">{t("menu.eos_now")}</dt>
                <dd className="tabular">{new Date(summary.data.now).toLocaleString()}</dd>
              </dl>
              <hr className="border-line" />
              <dl className="grid grid-cols-2 gap-y-1 text-sm">
                <dt className="text-ink-muted">{t("menu.eos_tx_count")}</dt>
                <dd className="tabular font-medium">{summary.data.tx_count}</dd>
                <dt className="text-ink-muted">{t("menu.eos_total_revenue")}</dt>
                <dd className="tabular font-semibold">
                  {formatTenne(summary.data.total_revenue_tenne)} TMT
                </dd>
              </dl>
              <hr className="border-line" />
              <dl className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-md border border-line-strong p-2">
                  <div className="text-ink-muted uppercase tracking-wider">
                    {t("menu.eos_issued")}
                  </div>
                  <div className="tabular font-semibold text-lg">
                    {summary.data.by_status.issued}
                  </div>
                </div>
                <div className="rounded-md border border-line-strong p-2">
                  <div className="text-ink-muted uppercase tracking-wider">
                    {t("menu.eos_paid")}
                  </div>
                  <div className="tabular font-semibold text-lg">{summary.data.by_status.paid}</div>
                </div>
                <div className="rounded-md border border-line-strong p-2">
                  <div className="text-ink-muted uppercase tracking-wider">
                    {t("menu.eos_void")}
                  </div>
                  <div className="tabular font-semibold text-lg">{summary.data.by_status.void}</div>
                </div>
              </dl>
              <div className="flex justify-end pt-2">
                <Button onClick={() => window.print()}>{t("menu.eos_print")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
